import { customFieldRepository } from '@webapp/core/data-access/repositories/CustomFieldRepository/CustomFieldRepository'
import {
  CustomField,
  type CustomFieldConstructorData,
} from '@webapp/core/entities/internal/CustomField'
import {
  type POPDFHiddenCustomField,
  SystemPreferenceKeys,
  SystemPreferencesRepository,
} from '@webapp/platform/iam'

const ACTIVE_CUSTOM_FIELD_PARAMS = {
  meta__is_active: 'True',
  page_size: 9999,
}

export interface POPDFCustomFieldDisplayRow {
  field: CustomField
  displayInPDF: boolean
}

export interface POPDFCustomFieldDisplay {
  poFields: POPDFCustomFieldDisplayRow[]
  orderItemFields: POPDFCustomFieldDisplayRow[]
}

// A never-set hidden map means every custom field is shown on the PDF (BHV-043)
const getHiddenMap = async (): Promise<POPDFHiddenCustomField[]> => {
  const preference = await SystemPreferencesRepository.getSystemPreference(
    SystemPreferenceKeys.PO_PDF_CUSTOM_FIELDS_HIDDEN
  )

  return preference?.value ?? []
}

/**
 * Join the active PO + order-item custom fields with the
 * `po-pdf-custom-fields-hidden` preference map (BHV-043): a field's display
 * toggle is on when it is absent from the map or its entry has
 * `hidden: false`.
 */
export const getPOPDFCustomFieldDisplay =
  async (): Promise<POPDFCustomFieldDisplay> => {
    const [poFields, orderItemFields, hiddenMap] = await Promise.all([
      customFieldRepository.getPurchaseOrderCustomFields(
        ACTIVE_CUSTOM_FIELD_PARAMS
      ),
      customFieldRepository.getOrderItemCustomFields(
        ACTIVE_CUSTOM_FIELD_PARAMS
      ),
      getHiddenMap(),
    ])

    const hiddenByFieldId = new Map(
      hiddenMap.map((entry) => [entry.customField, entry.hidden])
    )

    const toRow = (
      field: CustomFieldConstructorData
    ): POPDFCustomFieldDisplayRow => ({
      field: new CustomField(field),
      displayInPDF: !(hiddenByFieldId.get(field.id) ?? false),
    })

    return {
      poFields: poFields.data.map(toRow),
      orderItemFields: orderItemFields.data.map(toRow),
    }
  }
